import React, { useState, useEffect } from 'react';
import { Input, Button, Menu as AntMenu, Grid, Table, Tooltip } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import './Lists.scss';
import Header from '../../components/Header/Header';
import { listService } from "../../services/ListService";
import { useLocation } from 'react-router-dom';
import ListStatusGuard from './ListStatusGuard';

const { Search } = Input;
const DEFAULT_PAGE_SIZE = 20;
const TAB_ITEMS = [
    { key: 'creeaza', label: 'Creează listă' },
    { key: 'gata', label: 'Gata de trimis' },
    { key: 'ofertare', label: 'Liste în ofertare' },
    { key: 'ofertate', label: 'Liste ofertate' },
    { key: 'procesareManuala', label: 'Procesare manuala' },
    { key: 'arhivate', label: 'Liste arhivate' },
];


const OfertedLists = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [lists, setLists] = useState([]);
    const [isListsLoading, setIsListsLoading] = useState(false);
    const [isCountsLoading, setIsCountsLoading] = useState(false);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const pathToKey = {
        '/liste-necesar': 'creeaza',
        '/gata-de-trimis': 'gata',
        '/liste-in-ofertare': 'ofertare',
        '/liste-ofertate': 'ofertate',
        '/procesare-manuala': 'procesareManuala',

        '/liste-arhivate': 'arhivate',

    };
    const selectedMenuKey = pathToKey[location.pathname];
    const [counts, setCounts] = useState({});

    useEffect(() => {
        setIsCountsLoading(true);

        listService.getCounts()
            .then(res => {
                if (res.data) {
                    setCounts(res.data.list_counts);

                }
            })
            .catch(err => {
                console.error("Eroare la încărcarea listelor:", err);
            })
            .finally(() => {
                setIsCountsLoading(false);
            });

    }, []);

    useEffect(() => {
        setIsListsLoading(true);
        listService.getLists('offered')
            .then(res => {
                if (res.data) {
                    setLists(res.data.necessity_lists);
                }
            })
            .catch(err => {
                console.error("Eroare la încărcarea listelor:", err);
            })
            .finally(() => {
                setIsListsLoading(false);
            });
    }, []);

    useEffect(() => {
        const trimmedQuery = searchQuery.trim();
        if (trimmedQuery.length === 0) {
            setSearchResults([]);
            setIsSearchLoading(false);
            return;
        }

        setIsSearchLoading(true);
        const searchTimeout = setTimeout(() => {
            listService.searchList(trimmedQuery, 1, 'offered')
                .then(res => {
                    if (res.data?.necessity_lists) {
                        setSearchResults(res.data.necessity_lists);
                        return;
                    }

                    setSearchResults([]);
                })
                .catch(err => {
                    console.error("Eroare la căutare:", err);
                    setSearchResults([]);
                })
                .finally(() => {
                    setIsSearchLoading(false);
                });
        }, 350);

        return () => clearTimeout(searchTimeout);
    }, [searchQuery]);

    const getCountLabel = (key) => {
        if (isCountsLoading) {
            return '...';
        }

        return counts?.[key] ?? 0;
    };

    const formatListDate = (value) => {
        if (!value) {
            return '—';
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return '—';
        }

        return parsedDate.toLocaleDateString('ro-RO');
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        setCurrentPage(1);
    };




    const listColumns = [
        {
            title: 'Număr listă',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Nume',
            dataIndex: 'title',
            key: 'title'

        },
        {
            title: 'Creata la',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 140,
            render: (value) => formatListDate(value),
        },
        {
            title: 'Ofertata pe',
            dataIndex: 'programmed_date',
            key: 'programmed_date',
            width: 120,
            render: (value) => formatListDate(value),

        },
        {
            title: 'Produse ofertate',
            dataIndex: 'bidding_stats',
            key: 'bidding_stats',
            width: 140,
            render: (_, record) => {
                const { items_with_bids, total_items } = record.bidding_stats || {};
                return `${items_with_bids || 0} / ${total_items || 0}`;
            }
        },

        {
            title: 'Acțiuni',
            key: 'actions',
            width: 180,
            render: (_, record) => {
                const canFinalValidate = ListStatusGuard.canFinalValidate(record.status);

                return (
                    <Tooltip title={canFinalValidate ? '' : 'Disponibil după închiderea ofertării'}>
                        <span>
                            <Button
                                type="primary"
                                disabled={!canFinalValidate}
                                onClick={() => navigate(`/liste-ofertate/verificare-finala/${record.id}`)}
                                icon={<CheckOutlined />}
                            >
                                Verificare finală
                            </Button>
                        </span>
                    </Tooltip>
                );
            },
        }
    ];


    const MobileList = () => {
        const dataSource = searchQuery.trim().length > 0 ? searchResults : lists;

        if (dataSource.length === 0) {
            return (
                <div className="list__empty-state">
                    <p className="list__empty-state-text">Nicio listă găsită.</p>
                </div>
            );
        }

        return (
            <div className="list__mobile-table-wrapper">
                <div className="list__mobile-table-header">
                    <div className="list__mobile-table-header-cell" style={{ width: '60px', flexShrink: 0 }}>Nr</div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-name">Nume</div>
                    <div className="list__mobile-table-header-cell" style={{ width: '100px', flexShrink: 0 }}>Ofertate</div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-actions" style={{ width: '90px', flexShrink: 0 }}>Opt</div>
                </div>
                <div className="list__mobile-list">
                    {dataSource.map((row) => (
                        <div key={row.id} className="list__mobile-card">
                            <div className="list__mobile-card-cell" style={{ width: '60px', flexShrink: 0 }}>{row.id}</div>
                            <div className="list__mobile-card-cell list__mobile-card-name" title={row.title}>{row.title}</div>
                            <div className="list__mobile-card-cell" style={{ width: '100px', flexShrink: 0 }}>
                                {(row.bidding_stats?.items_with_bids || 0)} / {(row.bidding_stats?.total_items || 0)}
                            </div>
                            <div className="list__mobile-card-cell list__mobile-card-actions" style={{ width: '90px', flexShrink: 0 }}>
                                <Button
                                    type="primary"
                                    size="small"
                                    disabled={!ListStatusGuard.canFinalValidate(row.status)}
                                    onClick={() => navigate(`/liste-ofertate/verificare-finala/${row.id}`)}
                                    icon={<CheckOutlined />}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className='standard products list'>
            <Header />


            <div className='standard__container list'>
                <Menu />
                <div className='standard__wrapper'>
                    <div style={{ padding: 24, minHeight: 280 }}>
                        <h2 className='standard__page-title'>Liste ofertate</h2>
                        <h4 className='standard__page-info'>
                            Aici urmaresti listele ofertate, poti extrage produsele fara oferte si analiza usor optiunile primite.
                        </h4>

                        <div className='list__search-box'>
                            <Search
                                className="list__search-input"
                                placeholder="Caută lista..."
                                onChange={(e) => handleSearch(e.target.value)}
                                value={searchQuery}
                                enterButton
                                allowClear
                            />

                        </div>
                        <div className='list__tabs-wrapper'>
                            <AntMenu
                                mode="horizontal"
                                selectedKeys={[selectedMenuKey]}
                                style={{ marginTop: 16 }}
                                onClick={(e) => {
                                    switch (e.key) {
                                        case 'creeaza':
                                            navigate('/liste-necesar');
                                            break;
                                        case 'gata':
                                            navigate('/gata-de-trimis');
                                            break;
                                        case 'ofertare':
                                            navigate('/liste-in-ofertare');
                                            break;
                                        case 'ofertate':
                                            navigate('/liste-ofertate');
                                            break;
                                        case 'procesareManuala':
                                            navigate('/procesare-manuala');
                                            break;
                                        case 'arhivate':
                                            navigate('/liste-arhivate');
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                items={TAB_ITEMS.map((item) => ({
                                    key: item.key,
                                    label: item.key === 'gata'
                                        ? `${item.label} (${getCountLabel('ready_to_send')})`
                                        : item.key === 'ofertare'
                                            ? `${item.label} (${getCountLabel('offering')})`
                                            : item.key === 'ofertate'
                                                ? `${item.label} (${getCountLabel('offered')})`
                                                : item.key === 'procesareManuala'
                                                    ? `${item.label} (${getCountLabel('manual_processing')})`
                                                    : item.key === 'arhivate'
                                                        ? `${item.label} (${getCountLabel('archived')})`
                                                        : item.label
                                }))}
                            />
                        </div>


                        <div className="list__table-wrapper">

                            <div className='list__table-content'>
                                {!isMobile ? (
                                    <Table
                                        dataSource={searchQuery.trim().length > 0 ? searchResults : lists}
                                        columns={listColumns}
                                        rowKey="id"
                                        loading={isListsLoading || isSearchLoading}
                                        locale={{
                                            emptyText: searchQuery.trim().length > 0
                                                ? 'Nicio listă nu corespunde căutării.'
                                                : 'Nu există liste ofertate.'
                                        }}
                                        pagination={{
                                            current: currentPage,
                                            pageSize: DEFAULT_PAGE_SIZE,
                                            showSizeChanger: false,
                                            onChange: (page) => setCurrentPage(page),
                                        }}
                                        scroll={{ x: true }}
                                    />
                                ) : (
                                    <MobileList />
                                )}

                            </div>
                        </div>

                    </div>
                </div>
            </div>


        </div>
    );
};

export default OfertedLists;